import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Stepper } from "@/components/wizard/Stepper";
import { ConsentStep } from "@/components/wizard/steps/ConsentStep";
import { PersonalStep } from "@/components/wizard/steps/PersonalStep";
import { AddressStep } from "@/components/wizard/steps/AddressStep";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Package as PackageIcon, CheckCircle2, FileText,
  UserPlus, X as XIcon, Users,
} from "lucide-react";

const emptyPersonal = {
  family_name: "", given_name: "", middle_name: "", date_of_birth: "",
  gender: "", social_security_number: "", alien_registration_number: "",
  email: "", phone: "",
};

const emptyState = {
  consent: { acknowledged: false, signature: "" },
  personal: { ...emptyPersonal },
  address: {
    street_address: "", apartment: "", city: "", state: "",
    zip_code: "", country: "",
  },
  dependents: [], // {relationship, personal_information, use_same_address}
};

export default function PackagePage() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [current, setCurrent] = useState(0);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [data, setData] = useState(emptyState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get("/packages/catalog")
      .then((res) => {
        const list = res.data.packages || [];
        setPackages(list);
        const preselect = searchParams.get("pkg");
        if (preselect && list.some((p) => p.id === preselect)) {
          setSelectedPackage(preselect);
        }
      })
      .catch(() => toast.error("Failed to load packages"));
  }, [searchParams]);

  useEffect(() => {
    if (user && typeof user === "object" && !data.personal.email) {
      setData((prev) => ({
        ...prev,
        personal: { ...prev.personal, email: user.email || "" },
        consent: { ...prev.consent, signature: prev.consent.signature || user.name || "" },
      }));
    }
  }, [user, data.personal.email]);

  const steps = useMemo(
    () => [
      { id: "package", title: t.packages.step_select },
      { id: "consent", title: t.wizard.step_consent },
      { id: "personal", title: t.packages.step_principal },
      { id: "address", title: t.wizard.step_address },
      { id: "dependents", title: t.packages.step_dependents },
    ],
    [t],
  );

  const addDependent = () => {
    setData((prev) => ({
      ...prev,
      dependents: [
        ...prev.dependents,
        {
          relationship: "spouse",
          personal_information: { ...emptyPersonal },
          use_same_address: true,
        },
      ],
    }));
  };

  const removeDependent = (index) => {
    setData((prev) => ({
      ...prev,
      dependents: prev.dependents.filter((_, i) => i !== index),
    }));
  };

  const updateDependent = (index, patch) => {
    setData((prev) => ({
      ...prev,
      dependents: prev.dependents.map((d, i) =>
        i === index ? { ...d, ...patch } : d,
      ),
    }));
  };

  const updateDependentPersonal = (index, patch) => {
    setData((prev) => ({
      ...prev,
      dependents: prev.dependents.map((d, i) =>
        i === index
          ? { ...d, personal_information: { ...d.personal_information, ...patch } }
          : d,
      ),
    }));
  };

  const validateCurrent = () => {
    const e = {};
    if (current === 0) {
      if (!selectedPackage) {
        toast.error(t.common.required);
        return false;
      }
      return true;
    }
    if (current === 1) {
      if (!data.consent.acknowledged) e.acknowledged = t.common.required;
      if (!data.consent.signature?.trim()) e.signature = t.common.required;
      setErrors({ consent: e });
      return Object.keys(e).length === 0;
    }
    if (current === 2) {
      if (!data.personal.family_name?.trim()) e.family_name = t.common.required;
      if (!data.personal.given_name?.trim()) e.given_name = t.common.required;
      if (!data.personal.date_of_birth) e.date_of_birth = t.common.required;
      setErrors({ personal: e });
      return Object.keys(e).length === 0;
    }
    if (current === 3) {
      ["street_address", "city", "state", "zip_code", "country"].forEach((k) => {
        if (!data.address[k]?.trim()) e[k] = t.common.required;
      });
      setErrors({ address: e });
      return Object.keys(e).length === 0;
    }
    if (current === 4) {
      // Validate each dependent has minimum fields
      for (let i = 0; i < data.dependents.length; i++) {
        const dep = data.dependents[i];
        if (!dep.personal_information.family_name?.trim() ||
            !dep.personal_information.given_name?.trim() ||
            !dep.personal_information.date_of_birth) {
          toast.error(`${t.packages.dependent_incomplete} #${i + 1}`);
          return false;
        }
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateCurrent()) return;
    if (current === steps.length - 1) {
      handleCreate();
    } else {
      setCurrent((c) => c + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    setCurrent((c) => Math.max(c - 1, 0));
    setErrors({});
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        package_id: selectedPackage,
        consent: {
          acknowledged: data.consent.acknowledged,
          signature: data.consent.signature,
        },
        personal_information: { ...data.personal },
        address_information: { ...data.address },
        form_specific_overrides: {},
        dependents: data.dependents.map((d) => ({
          relationship: d.relationship,
          personal_information: d.personal_information,
          address_information: d.use_same_address ? null : d.address_information,
        })),
      };
      const res = await api.post("/packages/create", payload);
      setCreated(res.data);
      toast.success(t.packages.created_success);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to create package");
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    // Group forms by applicant
    const grouped = created.forms.reduce((acc, f) => {
      const key = `${f.applicant_role || "principal"}-${f.applicant_index ?? 0}`;
      if (!acc[key]) {
        acc[key] = {
          role: f.applicant_role || "principal",
          index: f.applicant_index ?? 0,
          name: f.applicant_name || created.principal_name,
          forms: [],
        };
      }
      acc[key].forms.push(f);
      return acc;
    }, {});

    return (
      <div className="mx-auto max-w-3xl px-6 py-16" data-testid="package-success">
        <div className="hairline bg-card p-10 md:p-14">
          <div className="flex h-14 w-14 items-center justify-center bg-[hsl(var(--primary))]">
            <CheckCircle2 className="h-7 w-7 text-[hsl(var(--primary-foreground))]" strokeWidth={2} />
          </div>
          <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {created.package_id} · {created.forms.length} {t.packages.forms_created}
          </div>
          <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">
            {t.packages.created_title}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {t.packages.created_description}
          </p>

          <div className="mt-8 space-y-6" data-testid="package-created-list">
            {Object.values(grouped).map((g) => (
              <div key={`${g.role}-${g.index}`} className="hairline">
                <div className="flex items-center justify-between border-b border-border bg-secondary/40 px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {t.packages.roles[g.role] || g.role}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">{g.name}</span>
                </div>
                <ul className="divide-y divide-border">
                  {g.forms.map((f) => (
                    <li key={f.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <div>
                          <div className="text-lg font-black tracking-tight">
                            {f.form_code}
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            {f.form_type}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none"
                        data-testid={`package-goto-form-${f.form_code}-${g.index}`}
                        onClick={() => navigate(`/wizard/${f.id}`)}
                      >
                        {t.common.edit}
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="package-goto-dashboard"
              className="h-11 rounded-none bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            >
              {t.success.dashboard}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="package-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--primary))]">
          {t.packages.overline}
        </div>
        <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">
          {t.packages.title}
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          {t.packages.subtitle}
        </p>
      </div>

      <div className="mb-10">
        <Stepper steps={steps} current={current} />
      </div>

      <div className="hairline bg-card p-8 md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
          >
            {current === 0 && (
              <div className="space-y-6" data-testid="package-select-step">
                <div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                    {t.packages.step_select}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t.packages.step_select_description}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {packages.map((p) => {
                    const selected = selectedPackage === p.id;
                    const name = lang === "es" ? p.name_es : p.name_pt;
                    const desc = lang === "es" ? p.description_es : p.description_pt;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`package-option-${p.id}`}
                        onClick={() => setSelectedPackage(p.id)}
                        className={`group flex flex-col gap-3 border p-6 text-left transition-colors ${
                          selected
                            ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.04]"
                            : "border-border bg-card hover:border-foreground/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 items-center justify-center ${
                              selected
                                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                : "bg-secondary text-foreground"
                            }`}>
                              <PackageIcon className="h-4 w-4" strokeWidth={2.2} />
                            </div>
                            <div className="text-xl font-black tracking-tight">
                              {name}
                            </div>
                          </div>
                          {selected && (
                            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--primary))]" />
                          )}
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {desc}
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {p.form_codes.map((c) => (
                            <span
                              key={c}
                              className="border border-border bg-card px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {current === 1 && (
              <ConsentStep
                value={data.consent}
                onChange={(v) => setData({ ...data, consent: v })}
                errors={errors.consent}
              />
            )}
            {current === 2 && (
              <PersonalStep
                value={data.personal}
                onChange={(v) => setData({ ...data, personal: v })}
                errors={errors.personal}
              />
            )}
            {current === 3 && (
              <AddressStep
                value={data.address}
                onChange={(v) => setData({ ...data, address: v })}
                errors={errors.address}
              />
            )}
            {current === 4 && (
              <div className="space-y-6" data-testid="package-dependents-step">
                <div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                    {t.packages.step_dependents}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t.packages.step_dependents_description}
                  </p>
                </div>

                {data.dependents.length === 0 && (
                  <div className="hairline bg-secondary/20 p-6 text-center">
                    <Users className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm text-muted-foreground">
                      {t.packages.no_dependents}
                    </p>
                  </div>
                )}

                {data.dependents.map((dep, idx) => (
                  <div key={idx} className="hairline p-5" data-testid={`dependent-${idx}`}>
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--primary))]">
                          {t.packages.dependent} #{idx + 1}
                        </span>
                      </div>
                      <button
                        type="button"
                        data-testid={`remove-dependent-${idx}`}
                        onClick={() => removeDependent(idx)}
                        className="text-muted-foreground hover:text-destructive"
                        title={t.common.delete}
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.packages.relationship}
                        </Label>
                        <Select
                          value={dep.relationship}
                          onValueChange={(v) => updateDependent(idx, { relationship: v })}
                        >
                          <SelectTrigger
                            data-testid={`dependent-${idx}-relationship`}
                            className="mt-2 h-11"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spouse">{t.packages.roles.spouse}</SelectItem>
                            <SelectItem value="child">{t.packages.roles.child}</SelectItem>
                            <SelectItem value="parent">{t.packages.roles.parent}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.personal.family_name} *
                        </Label>
                        <Input
                          className="mt-2 h-11"
                          data-testid={`dependent-${idx}-family-name`}
                          value={dep.personal_information.family_name}
                          onChange={(e) => updateDependentPersonal(idx, { family_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.personal.given_name} *
                        </Label>
                        <Input
                          className="mt-2 h-11"
                          data-testid={`dependent-${idx}-given-name`}
                          value={dep.personal_information.given_name}
                          onChange={(e) => updateDependentPersonal(idx, { given_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.personal.date_of_birth} *
                        </Label>
                        <Input
                          type="date"
                          className="mt-2 h-11"
                          data-testid={`dependent-${idx}-dob`}
                          value={dep.personal_information.date_of_birth}
                          onChange={(e) => updateDependentPersonal(idx, { date_of_birth: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.personal.gender}
                        </Label>
                        <Select
                          value={dep.personal_information.gender}
                          onValueChange={(v) => updateDependentPersonal(idx, { gender: v })}
                        >
                          <SelectTrigger className="mt-2 h-11">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MALE">{t.personal.gender_options.MALE}</SelectItem>
                            <SelectItem value="FEMALE">{t.personal.gender_options.FEMALE}</SelectItem>
                            <SelectItem value="OTHER">{t.personal.gender_options.OTHER}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.personal.a_number}
                        </Label>
                        <Input
                          className="mt-2 h-11 font-mono"
                          data-testid={`dependent-${idx}-anumber`}
                          value={dep.personal_information.alien_registration_number}
                          onChange={(e) => updateDependentPersonal(idx, { alien_registration_number: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addDependent}
                  data-testid="add-dependent-button"
                  className="w-full rounded-none border-dashed border-foreground/40 py-6"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t.packages.add_dependent}
                </Button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={goBack}
          disabled={current === 0}
          data-testid="package-back-button"
          className="h-11 rounded-none border-foreground/40"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.common.back}
        </Button>
        <Button
          onClick={goNext}
          disabled={submitting}
          data-testid="package-next-button"
          className="h-11 rounded-none bg-[hsl(var(--primary))] px-6 hover:bg-[hsl(var(--primary))]/90"
        >
          {current === steps.length - 1
            ? submitting
              ? t.common.loading
              : t.packages.create_cta
            : t.common.next}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
